import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ui/components/ui/dialog';
import { Button } from '@ui/components/ui/button';
import { Checkbox } from '@ui/components/ui/checkbox';
import DateTimeField from '@app/components/shared/reportModal/DateTimeField';
import { Incident, resolveError, toLocalInput } from './sources/incidents';

/** Saying when an incident was fixed, and closing it in the same write when that is what was asked for. */
const IncidentResolveDialog = ({
  incident,
  refusal,
  onResolve,
  onCancel,
}: {
  incident: Incident | null;
  /** A rule the server refused this write on. The dialog stays open, so it is said here and not only in a toast. */
  refusal?: string | null;
  /** The moment it was fixed, and whether we are also done with the record. */
  onResolve: (atLocal: string, alsoClose: boolean) => void;
  onCancel: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const [at, setAt] = useState('');
  const [alsoClose, setAlsoClose] = useState(false);

  // A box left ticked from the last incident would close this one on a single click.
  useEffect(() => {
    if (!incident) return;
    setAt(toLocalInput(new Date().toISOString()));
    setAlsoClose(false);
  }, [incident?.id]);

  const error = incident ? resolveError(incident, at) : null;

  return (
    <Dialog open={!!incident} onOpenChange={open => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('incidents.resolveTitle')}</DialogTitle>
          <DialogDescription>{incident?.title}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-1.5">
          <DateTimeField
            id="incident-resolved-at"
            label={t('incidents.resolvedAt')}
            value={at}
            onChange={setAt}
            invalid={!!error}
            // Nothing was fixed tomorrow.
            disabledDays={{ after: new Date() }}
          />
          {error ? (
            <p className="text-[11px] text-destructive">{t(`incidents.error.${error}`)}</p>
          ) : (
            <p className="text-[11px] text-muted-foreground">{t('incidents.resolveMeaning')}</p>
          )}
        </div>

        <div className="flex items-start gap-2">
          <Checkbox
            id="incident-also-close"
            className="mt-0.5"
            checked={alsoClose}
            onCheckedChange={value => setAlsoClose(value === true)}
          />
          <label
            id="incident-also-close-label"
            htmlFor="incident-also-close"
            className="cursor-pointer text-sm text-foreground"
          >
            {t('incidents.alsoClose')}
            <span className="block text-[11px] text-muted-foreground">{t('incidents.closeMeaning')}</span>
          </label>
        </div>

        {refusal && <p className="text-[11px] text-destructive">{refusal}</p>}

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            {t('actions.cancel')}
          </Button>
          <Button disabled={!!error} onClick={() => onResolve(at, alsoClose)}>
            {t(alsoClose ? 'incidents.resolveAndCloseConfirm' : 'incidents.resolveConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IncidentResolveDialog;
